import axios from 'axios';
import { apiBase } from './apiBase';
import {
  FundChartResponse,
  FundHoldingsResponse,
  FundInfoResponse,
  FundLipperRatingResponse,
  FundPerformanceResponse,
  FundQuoteResponse,
} from '../types/fundsummary.type';

export const getFundInfo = ({
  token,
  ric,
  saleInCountryCode = 'US',
}: {
  token: string;
  ric: string;
  saleInCountryCode?: string;
}): Promise<FundInfoResponse | undefined> => {
  return new Promise(async (resolve, reject) => {
    axios
      .get(`${apiBase()}/fund/info`, {
        params: { token, ric, saleInCountryCode },
      })
      .then((res) => {
        resolve(res.data ?? undefined);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getFundQuote = ({
  token,
  ric,
  saleInCountryCode = 'US',
}: {
  token: string;
  ric: string;
  saleInCountryCode?: string;
}): Promise<FundQuoteResponse | undefined> => {
  return new Promise(async (resolve, reject) => {
    axios
      .get(`${apiBase()}/fund/quote`, {
        params: { token, ric, saleInCountryCode },
      })
      .then((res) => {
        resolve(res.data ?? undefined);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getFundPerformance = ({
  token,
  ric,
  saleInCountryCode = 'US',
}: {
  token: string;
  ric: string;
  saleInCountryCode?: string;
}): Promise<FundPerformanceResponse | undefined> => {
  return new Promise(async (resolve, reject) => {
    axios
      .get(`${apiBase()}/fund/performance`, {
        params: { token, ric, saleInCountryCode },
      })
      .then((res) => {
        resolve(res.data ?? undefined);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getFundLipperRating = ({
  token,
  ric,
  saleInCountryCode = 'US',
}: {
  token: string;
  ric: string;
  saleInCountryCode?: string;
}): Promise<FundLipperRatingResponse | undefined> => {
  return new Promise(async (resolve, reject) => {
    axios
      .get(`${apiBase()}/fund/lipper-rating`, {
        params: { token, ric, saleInCountryCode },
      })
      .then((res) => {
        resolve(res.data ?? undefined);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getFundHoldings = ({
  token,
  ric,
  saleInCountryCode = 'US',
}: {
  token: string;
  ric: string;
  saleInCountryCode?: string;
}): Promise<FundHoldingsResponse | undefined> => {
  return new Promise(async (resolve, reject) => {
    axios
      .get(`${apiBase()}/fund/holdings`, {
        params: { token, ric, saleInCountryCode },
      })
      .then((res) => {
        resolve(res.data ?? undefined);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Fund chart endpoints share an identical shape and only differ by `type`:
 * - `nav` : Net Asset Value
 * - `tna` : Total Net Asset
 * - `mid` : Lipper Index
 * `from` / `to` use the `YYYYMMDD` format.
 */
export const getFundChart = ({
  token,
  ric,
  type,
  from,
  to,
  saleInCountryCode = 'US',
}: {
  token: string;
  ric: string;
  type: 'nav' | 'tna' | 'mid';
  from?: string;
  to?: string;
  saleInCountryCode?: string;
}): Promise<FundChartResponse | undefined> => {
  return new Promise(async (resolve, reject) => {
    const params: Record<string, string> = {
      token,
      ric,
      saleInCountryCode,
    };
    // `from` is omitted (e.g. for the "All" range) to fetch the full history.
    if (from) params.from = from;
    if (to) params.to = to;
    axios
      .get(`${apiBase()}/fund/charts/${type}`, {
        params,
      })
      .then((res) => {
        resolve(res.data ?? undefined);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
